const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + "api/v1" || "http://localhost:3001/api";

export interface TokenResponse {
    token: string;
    channelName: string;
    uid: number;
    appId: string;
    expireTime: number;
    timestamp: number;
}

export interface TokenRequest {
    channelName: string;
    uid?: number;
    role?: "publisher" | "subscriber";
    expireTime?: number;
}

export interface ServerConfig {
    appId: string;
    isConfigured: boolean;
    timestamp: number;
}

export interface HealthCheck {
    status: string;
    service: string;
    timestamp: string;
    configured: boolean;
}

export class AgoraTokenService {
    /**
     * Generate a new Agora RTC token from the backend server
     */
    static async generateToken(request: TokenRequest): Promise<TokenResponse> {
        try {
            console.log("🔑 Requesting token from server:", request);

            const response = await fetch(`${API_BASE_URL}/agora/token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(request),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const tokenResponse = await response.json();
            console.log("✅ Token received from server:", {
                channelName: tokenResponse.channelName,
                uid: tokenResponse.uid,
                expireTime: new Date(tokenResponse.expireTime * 1000).toISOString()
            });

            return tokenResponse;
        } catch (error) {
            console.error("❌ Failed to generate token:", error);
            throw error;
        }
    }

    /**
     * Get token using GET request (alternative method)
     */
    static async generateTokenGet(
        channelName: string,
        uid?: number,
        role?: "publisher" | "subscriber",
        expireTime?: number
    ): Promise<TokenResponse> {
        try {
            const params = new URLSearchParams({
                channelName,
                ...(uid !== undefined && { uid: uid.toString() }),
                ...(role && { role }),
                ...(expireTime && { expireTime: expireTime.toString() }),
            });

            const response = await fetch(`${API_BASE_URL}/agora/token?${params}`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Failed to generate token (GET):", error);
            throw error;
        }
    }

    /**
     * Get server configuration including App ID
     */
    static async getServerConfig(): Promise<ServerConfig> {
        try {
            const response = await fetch(`${API_BASE_URL}/agora/config`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error("Failed to get server config:", error);
            throw error;
        }
    }

    /**
     * Check if the token server is healthy and configured
     */
    static async healthCheck(): Promise<HealthCheck> {
        try {
            const response = await fetch(`${API_BASE_URL}/agora/health`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error("Health check failed:", error);
            throw error;
        }
    }

    /**
     * Check if the token server is available
     */
    static async isServerAvailable(): Promise<boolean> {
        try {
            await this.healthCheck();
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Generate token with automatic fallback to temp token
     */
    static async generateTokenWithFallback(
        channelName: string,
        uid?: number
    ): Promise<{ token: string; source: "server" | "temp" }> {
        try {
            // Try to get token from server first
            const tokenResponse = await this.generateToken({
                channelName,
                uid: uid || 0,
                role: "publisher",
                expireTime: 3600 // 1 hour
            });

            return {
                token: tokenResponse.token,
                source: "server"
            };
        } catch (error) {
            console.warn("⚠️ Failed to get token from server, checking for temp token fallback...");

            // Fallback to temp token if available
            const TEMP_TOKEN = process.env.REACT_APP_AGORA_TEMP_TOKEN;
            if (TEMP_TOKEN) {
                console.log("🧪 Using fallback temporary token");
                return {
                    token: TEMP_TOKEN,
                    source: "temp"
                };
            }

            // No fallback available
            throw new Error("No token server available and no temporary token configured");
        }
    }
}
